import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button, Paper } from '@mui/material';
import AUHeader from '../components/AUHeader';
import VideoAccessForm from '../components/VideoAccessForm';
import DescargaPDF from '../components/DescargaPDF';
import { hasValidVideoAccess, setStoredVideoAccess } from '../utils/videoAccessStorage';
import { submitLead } from '../utils/submitLead';

const BLUE = '#1A7FFF';
const BLUE_HOVER = '#1568D9';
const BLUE_SOFT = '#EBF3FF';
const DARK = '#0F1117';
const GRAY = '#64748B';
const BORDER = '#E2E8F0';
const GRAY_LIGHT = '#F8FAFC';

const PDF_URL = '/recursos/split-5-dias-au-coaching.pdf';

// ---- contenido del split ----
const WHY = [
  {
    title: 'Calidad sobre cantidad',
    desc: 'El trabajo que progresa siempre le gana al que se acumula. 2 series que avanzan cada semana valen más que 5 estancadas.',
  },
  {
    title: 'Recuperación constante',
    desc: 'Con dos días de descanso bien distribuidos llegas recuperado a cada sesión y el progreso se sostiene en el tiempo.',
  },
  {
    title: 'Cada músculo 2-3 veces por semana',
    desc: 'Pecho, espalda, hombro, bíceps y tríceps aparecen en los tres días de upper. Frecuencia alta con el volumen repartido de forma inteligente.',
  },
];

const WEEK = [
  { day: 'Día 1', name: 'Upper 1', muscles: ['Core', 'Pecho', 'Espalda', 'Hombro', 'Brazos'] },
  { day: 'Día 2', name: 'Lower 1', muscles: ['Femoral', 'Cuádriceps', 'Glúteo', 'Pantorrilla', 'Hombro'] },
  { day: 'Día 3', name: 'Descanso', rest: true },
  { day: 'Día 4', name: 'Upper 2', muscles: ['Core', 'Espalda', 'Pecho', 'Hombro', 'Brazos'] },
  { day: 'Día 5', name: 'Lower 2', muscles: ['Femoral', 'Cad. post.', 'Aductor', 'Cuádriceps', 'Pantorrilla', 'Hombro'] },
  { day: 'Día 6', name: 'Upper 3', muscles: ['Pecho', 'Espalda', 'Brazos', 'Hombro'] },
  { day: 'Día 7', name: 'Descanso', rest: true },
];

const STEPS = [
  { badge: 'Paso 1', title: 'Empezar en el rango alto', desc: 'Con un peso nuevo, llega al límite superior del rango (ej. 12 de 10-12) con buena técnica y repeticiones en reserva. No llegues al fallo.' },
  { badge: 'Paso 2', title: 'Ganar repeticiones semana a semana', desc: 'Si completas todas las series dentro del rango con buena ejecución, la próxima sesión intenta una repetición más. Acumula hasta superar el tope.' },
  { badge: 'Paso 3', title: 'Superar el rango y subir el peso', desc: 'Al superar el tope (ej. 13 reps en un rango de 10-12), la próxima sesión sube el peso lo mínimo posible (+1-2.5 kg) y vuelve al rango bajo.' },
  { badge: 'Paso 4', title: 'Repetir el ciclo', desc: 'Con el peso nuevo, empieza de nuevo en el rango alto con buena técnica y repeticiones en reserva. El proceso se repite indefinidamente.' },
];

const VOLUME = [
  { muscle: 'Pecho', series: '8 series' },
  { muscle: 'Espalda', series: '10 series' },
  { muscle: 'Hombro', series: '14 series' },
  { muscle: 'Bíceps', series: '9 series' },
  { muscle: 'Tríceps', series: '9 series' },
  { muscle: 'Core', series: '8 series' },
  { muscle: 'Cuádriceps', series: '8 series' },
  { muscle: 'Femoral', series: '4 series' },
  { muscle: 'Cadena posterior', series: '2 series' },
  { muscle: 'Aductor', series: '2 series' },
  { muscle: 'Pantorrilla', series: '6 series' },
  { muscle: 'Glúteo', series: 'indirecto' },
];

const DAYS = [
  {
    id: 'Día 1', name: 'Upper 1', focus: 'Pecho · Espalda · Hombro · Bíceps · Tríceps',
    rows: [
      { ex: 'Cable crunch', ser: '2', reps: '10-12', rest: '1-2 min', note: 'Superset con plancha lateral. Empieza con abdomen para activar el core.' },
      { ex: 'Plancha lateral', ser: '2', reps: 'seg', rest: '1-2 min', note: 'Superset con cable crunch. Registra los segundos para progresar.' },
      { ex: 'Press plano en máquina', ser: '2', reps: '8-10', rest: '3-5 min', note: 'Foco pecho. Variante: Smith, barra libre o cualquier press plano sobrecargable.' },
      { ex: 'Press inclinado bajo con mancuernas', ser: '2', reps: '10-12', rest: '3-5 min', note: 'Inclinación 15-30°. Variante: máquina o Smith inclinado.' },
      { ex: 'Remo pecho apoyado en máquina', ser: '2', reps: '10-12', rest: '3-5 min', note: 'Espalda alta, codos abiertos. Variante: T-bar row o cables.' },
      { ex: 'Vuelo crucifix hombro posterior', ser: '3', reps: '10-12', rest: '2-4 min', note: 'Con cuff en cable.' },
      { ex: 'Extensión trícep cruzada unilateral', ser: '3', reps: '10-12', rest: '2-4 min', note: 'Con cuff en cable.' },
      { ex: 'Curl de bícep standaway', ser: '3', reps: '10-12', rest: '2-4 min', note: 'Codos detrás del cuerpo (bayesian). Puede ser unilateral.' },
    ],
  },
  {
    id: 'Día 2', name: 'Lower 1', focus: 'Cuádriceps · Femoral · Glúteo · Pantorrilla · Hombro',
    rows: [
      { ex: 'Leg curl sentado', ser: '2', reps: '10-12', rest: '2-4 min', note: 'Femoral. Variante: leg curl acostado.' },
      { ex: 'Hack squat', ser: '2', reps: '8-10', rest: '3-5 min', note: 'Foco cuádriceps. Variante: péndulo, Smith o barra libre high bar.' },
      { ex: 'Prensa unilateral', ser: '2', reps: '10-12', rest: '3-5 min', note: 'Cuádriceps y glúteo unilateral. Variante: búlgara.' },
      { ex: 'Leg extension', ser: '2', reps: '12-15', rest: '2-4 min', note: 'Aislamiento de cuádriceps.' },
      { ex: 'Calf raise en prensa', ser: '3', reps: '10-12', rest: '2-4 min', note: 'Cualquier calf raise.' },
      { ex: 'Vuelo lateral unilateral', ser: '3', reps: '10-12', rest: '2-4 min', note: 'Cable + cuff. Reparte el volumen de hombro de la semana.' },
    ],
  },
  {
    id: 'Día 4', name: 'Upper 2', focus: 'Espalda · Pecho · Hombro · Bíceps · Tríceps · Core',
    rows: [
      { ex: 'Leg raises', ser: '2', reps: '10-12', rest: '1-2 min', note: 'Superset con plancha. Empieza con abdomen.' },
      { ex: 'Plancha', ser: '2', reps: 'seg', rest: '1-2 min', note: 'Superset con leg raises. Registra los segundos.' },
      { ex: 'Pulldown bilateral', ser: '2', reps: '10-12', rest: '3-5 min', note: 'Codos abiertos. Variante: pull-ups o cualquier pulldown.' },
      { ex: 'Remo dorsal unilateral', ser: '2', reps: '12-14', rest: '3-5 min', note: 'Foco dorsal, en máquina o con mancuerna.' },
      { ex: 'Press inclinado bajo en Smith', ser: '2', reps: '8-10', rest: '3-5 min', note: 'Pecho, 15° de inclinación. Variante: barra libre microprogresable.' },
      { ex: 'Press de hombro en máquina', ser: '2', reps: '8-10', rest: '3-5 min', note: 'Variante: press con mancuernas.' },
      { ex: 'Curl predicador unilateral máquina', ser: '3', reps: '10-12', rest: '2-4 min', note: 'Variante: predicador con mancuerna.' },
      { ex: 'Extensión trícep sobre la cabeza', ser: '3', reps: '10-12', rest: '2-4 min', note: 'Con cuff. Cualquier extensión overhead.' },
    ],
  },
  {
    id: 'Día 5', name: 'Lower 2', focus: 'Femoral · Glúteo · Cadena posterior · Cuádriceps · Aductor · Pantorrilla',
    rows: [
      { ex: 'Leg curl acostado', ser: '2', reps: '10-12', rest: '2-4 min', note: 'Femoral. Variante: leg curl sentado.' },
      { ex: 'RDL', ser: '2', reps: '8-10', rest: '3-5 min', note: 'Cadena posterior. Variante: buenos días o hip thrust.' },
      { ex: 'Aductor en máquina', ser: '2', reps: '12-15', rest: '2-4 min', note: 'Trabajo directo de aductor.' },
      { ex: 'Prensa', ser: '2', reps: '10-12', rest: '3-5 min', note: 'Cuádriceps y glúteo. Pies altos para más glúteo y femoral.' },
      { ex: 'Calf raise sentado', ser: '3', reps: '10-12', rest: '2-4 min', note: 'Cualquier calf raise.' },
      { ex: 'Vuelo lateral con cable por detrás', ser: '3', reps: '10-12', rest: '2-4 min', note: 'Cable + cuff por detrás del cuerpo. Completa el volumen de hombro.' },
    ],
  },
  {
    id: 'Día 6', name: 'Upper 3', focus: 'Pecho · Espalda · Hombro · Bíceps · Tríceps',
    rows: [
      { ex: 'Press inclinado en máquina', ser: '2', reps: '10-12', rest: '3-5 min', note: 'Pecho, inclinación 15-30°.' },
      { ex: 'Remo pecho apoyado con mancuernas', ser: '2', reps: '12-14', rest: '3-5 min', note: 'Espalda alta. Variante: T-bar o máquina.' },
      { ex: 'Pulldown dorsal unilateral', ser: '2', reps: '10-12', rest: '3-5 min', note: 'Foco dorsal, trabajo unilateral.' },
      { ex: 'Curl bícep unilateral mancuerna', ser: '3', reps: '10-12', rest: '2-4 min', note: 'Control de la fase excéntrica.' },
      { ex: 'Extensión trícep doble cuerda', ser: '3', reps: '10-12', rest: '2-4 min', note: 'Codos hacia abajo. Cualquier extensión válida.' },
      { ex: 'Vuelo lateral bilateral en cable', ser: '3', reps: '10-12', rest: '2-4 min', note: 'Con cuffs. Cierra el volumen de hombro lateral de la semana.' },
    ],
  },
];

// ---- subcomponentes ----
function SectionTitle({ children }) {
  return (
    <Typography sx={{
      fontFamily: 'Kanit, sans-serif', fontWeight: 800, color: DARK,
      fontSize: { xs: '1.3rem', md: '1.6rem' }, mb: 2.5, mt: 1,
    }}>
      {children}
    </Typography>
  );
}

function WhyCard({ title, desc }) {
  return (
    <Paper elevation={0} sx={{
      flex: 1, p: 2.5, borderRadius: 3, bgcolor: '#fff',
      border: `1px solid ${BORDER}`, borderLeft: `4px solid ${BLUE}`,
    }}>
      <Typography sx={{ fontWeight: 700, color: BLUE_HOVER, fontFamily: 'Kanit, sans-serif', mb: 0.75 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: 14, color: GRAY, fontFamily: 'Kanit, sans-serif' }}>
        {desc}
      </Typography>
    </Paper>
  );
}

function WeekCard({ day, name, muscles, rest }) {
  return (
    <Paper elevation={0} sx={{
      borderRadius: 2, overflow: 'hidden', bgcolor: rest ? GRAY_LIGHT : '#fff',
      border: `1px solid ${BORDER}`,
    }}>
      <Box sx={{ bgcolor: rest ? '#94A3B8' : BLUE, py: 0.5, textAlign: 'center' }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: '#fff', fontFamily: 'Kanit, sans-serif', textTransform: 'uppercase' }}>
          {day}
        </Typography>
      </Box>
      <Box sx={{ p: 1.25, textAlign: 'center' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: rest ? GRAY : BLUE_HOVER, fontFamily: 'Kanit, sans-serif', mb: 0.5, textTransform: 'uppercase' }}>
          {name}
        </Typography>
        {rest ? (
          <Typography sx={{ fontSize: 12, color: GRAY, fontFamily: 'Kanit, sans-serif' }}>Recuperación</Typography>
        ) : (
          muscles.map((m) => (
            <Typography key={m} sx={{ fontSize: 12, color: GRAY, fontFamily: 'Kanit, sans-serif', lineHeight: 1.55 }}>
              {m}
            </Typography>
          ))
        )}
      </Box>
    </Paper>
  );
}

function StepCard({ badge, title, desc }) {
  return (
    <Paper elevation={0} sx={{ flex: 1, p: 2, borderRadius: 3, bgcolor: '#fff', border: `1px solid ${BORDER}` }}>
      <Box sx={{ display: 'inline-block', bgcolor: BLUE, borderRadius: 1.5, px: 1.25, py: 0.4, mb: 1 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#fff', fontFamily: 'Kanit, sans-serif', textTransform: 'uppercase' }}>
          {badge}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: BLUE_HOVER, fontFamily: 'Kanit, sans-serif', mb: 0.5 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: 13, color: GRAY, fontFamily: 'Kanit, sans-serif' }}>
        {desc}
      </Typography>
    </Paper>
  );
}

function ExerciseRow({ row, index }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1.5, px: { xs: 1.5, md: 2 },
      bgcolor: index % 2 === 0 ? BLUE_SOFT : '#fff',
    }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: GRAY, fontFamily: 'Kanit, sans-serif', minWidth: 16 }}>
        {index + 1}
      </Typography>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontWeight: 700, color: DARK, fontFamily: 'Kanit, sans-serif', fontSize: 15 }}>
          {row.ex}
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: GRAY, fontFamily: 'Kanit, sans-serif' }}>
          {row.note}
        </Typography>
      </Box>
      <Box sx={{ textAlign: 'right', minWidth: { xs: 78, md: 92 } }}>
        <Typography sx={{ fontWeight: 800, color: BLUE_HOVER, fontFamily: 'Kanit, sans-serif', fontSize: 15, whiteSpace: 'nowrap' }}>
          {row.ser} × {row.reps}
        </Typography>
        <Typography sx={{ fontSize: 12, color: GRAY, fontFamily: 'Kanit, sans-serif', whiteSpace: 'nowrap' }}>
          {row.rest}
        </Typography>
      </Box>
    </Box>
  );
}

function DayBlock({ day }) {
  return (
    <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: `1px solid ${BORDER}`, mb: 2.5 }}>
      <Box sx={{
        bgcolor: BLUE_HOVER, px: { xs: 1.5, md: 2 }, py: 1.25,
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5,
      }}>
        <Typography sx={{ fontWeight: 800, color: '#fff', fontFamily: 'Kanit, sans-serif', fontSize: 16 }}>
          {day.id} · {day.name}
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: '#CCE5FF', fontFamily: 'Kanit, sans-serif' }}>
          {day.focus}
        </Typography>
      </Box>
      {day.rows.map((row, i) => <ExerciseRow key={row.ex} row={row} index={i} />)}
    </Paper>
  );
}

const FiveDaySplitPage = () => {
  const navigate = useNavigate();
  const [unlocked, setUnlocked] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);

  const handleUnlock = () => {
    if (hasValidVideoAccess()) {
      setUnlocked(true);
    } else {
      setShowLeadForm(true);
    }
  };

  const handleLeadSubmit = async (data) => {
    setShowLeadForm(false);
    setStoredVideoAccess(data);
    await submitLead({ data, source: '5-day-split' });
    setUnlocked(true);
  };

  return (
    <Box sx={{ bgcolor: GRAY_LIGHT, fontFamily: 'Kanit, sans-serif', minHeight: '100vh' }}>
      <AUHeader />

      <Container maxWidth="lg" sx={{ pt: { xs: 4, md: 6 }, pb: { xs: 6, md: 10 } }}>
        {/* Hero */}
        <Typography sx={{
          fontSize: 15, fontWeight: 700, letterSpacing: 2.5, color: BLUE,
          textTransform: 'uppercase', mb: 0.75, fontFamily: 'Kanit, sans-serif',
        }}>
          Material gratis
        </Typography>
        <Typography component="h1" sx={{
          fontFamily: 'Kanit, sans-serif', fontWeight: 800,
          fontSize: { xs: '2rem', md: '2.8rem' }, color: DARK, mb: 1,
        }}>
          El mejor split de 5 días
        </Typography>
        <Typography sx={{ color: GRAY, mb: 4, maxWidth: 680, fontFamily: 'Kanit, sans-serif' }}>
          El split de 5 días con el que más resultados he visto en atletas avanzados: alta frecuencia, volumen
          repartido con cabeza y recuperación constante. Ejercicios, series, repeticiones y descansos, listo para el gym.
        </Typography>

        {!unlocked ? (
          /* Gate — todo el material vive detrás del form */
          <Paper elevation={0} sx={{
            borderRadius: 3, border: `1px solid ${BLUE}`, bgcolor: '#fff',
            p: { xs: 3, md: 5 }, textAlign: 'center', mt: 1,
          }}>
            <Typography sx={{ fontWeight: 800, color: DARK, fontFamily: 'Kanit, sans-serif', fontSize: { xs: '1.4rem', md: '1.9rem' }, mb: 1 }}>
              Desbloquea el split completo
            </Typography>
            <Typography sx={{ color: GRAY, fontFamily: 'Kanit, sans-serif', mb: 3, maxWidth: 560, mx: 'auto' }}>
              Déjanos tus datos y accede al instante a todo el material:
            </Typography>
            <Box sx={{ maxWidth: 500, mx: 'auto', textAlign: 'left', mb: 3.5 }}>
              {[
                'Estructura semanal y volumen por grupo muscular',
                'Los 5 días, ejercicio por ejercicio: series, reps, descansos y variantes',
                'Cómo aplicar sobrecarga progresiva para no estancarte',
                'El PDF descargable para llevarlo al gym',
              ].map((item) => (
                <Box key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 1.25 }}>
                  <Box sx={{ color: BLUE, fontWeight: 800, fontFamily: 'Kanit, sans-serif', fontSize: 18, lineHeight: 1.35 }}>✓</Box>
                  <Typography sx={{ color: DARK, fontFamily: 'Kanit, sans-serif', fontSize: 15 }}>{item}</Typography>
                </Box>
              ))}
            </Box>
            <Button
              variant="contained" disableElevation onClick={handleUnlock}
              sx={{
                py: 1.5, px: 5, fontFamily: 'Kanit, sans-serif', fontWeight: 700, fontSize: 16,
                textTransform: 'none', bgcolor: BLUE, '&:hover': { bgcolor: BLUE_HOVER },
              }}
            >
              Ver el split completo
            </Button>
            <Typography sx={{ color: GRAY, fontFamily: 'Kanit, sans-serif', fontSize: 12.5, mt: 2 }}>
              Gratis. Solo te pedimos tus datos para enviártelo y acompañarte en el proceso.
            </Typography>
          </Paper>
        ) : (
        <>
        <DescargaPDF
          url={PDF_URL}
          titulo="Descarga el split de 5 días en PDF"
          bajada="La rutina completa con series, repeticiones y descansos. Abajo la tienes desarrollada."
        />
        {/* Disclaimer — marco del documento, igual que en el PDF */}
        <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: `1px solid ${BLUE}`, bgcolor: BLUE_SOFT, mb: 5 }}>
          <Box sx={{ bgcolor: BLUE, px: { xs: 2, md: 3 }, py: 1.25 }}>
            <Typography sx={{ fontWeight: 800, color: '#fff', fontFamily: 'Kanit, sans-serif', fontSize: { xs: 15, md: 17 } }}>
              OJO: esto es una rutina, no un programa
            </Typography>
          </Box>
          <Box sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
            <Typography sx={{ color: DARK, fontFamily: 'Kanit, sans-serif', fontSize: 14.5, mb: 1.5 }}>
              Muchos te cobran por una rutina como esta. Nosotros te la damos gratis, porque para nosotros es solo el punto de partida.
            </Typography>
            <Typography sx={{ color: DARK, fontFamily: 'Kanit, sans-serif', fontSize: 14.5, mb: 1.5 }}>
              Una rutina es una lista de ejercicios. Un programa es otra cosa: progresión planificada por fases, ajustes de
              volumen según cómo responde tu cuerpo y un objetivo claro en el tiempo. Esa periodización, con seguimiento real,
              es lo que hacemos en AU Coaching, y es lo que sostiene el progreso por años.
            </Typography>
            <Typography sx={{ color: BLUE_HOVER, fontFamily: 'Kanit, sans-serif', fontWeight: 700, fontSize: 15 }}>
              Una rutina te mantiene; un programa te hace crecer. AU Coaching es mucho más que rutinas.
            </Typography>
          </Box>
        </Paper>

        {/* ¿Por qué este split? */}
        <SectionTitle>¿Por qué este split?</SectionTitle>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 5 }}>
          {WHY.map((w) => <WhyCard key={w.title} {...w} />)}
        </Box>

        {/* Estructura semanal */}
        <SectionTitle>Estructura semanal</SectionTitle>
        <Box sx={{
          display: 'grid', gap: 1.25, mb: 5,
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)', md: 'repeat(7, 1fr)' },
        }}>
          {WEEK.map((d) => <WeekCard key={d.day} {...d} />)}
        </Box>

        {/* Cómo aplicar sobrecarga progresiva */}
        <SectionTitle>Cómo aplicar sobrecarga progresiva</SectionTitle>
        <Typography sx={{ color: GRAY, mb: 2.5, maxWidth: 760, fontFamily: 'Kanit, sans-serif', fontSize: 14.5 }}>
          La base de cualquier resultado: que la carga que mueve tu músculo suba con el tiempo, con buena técnica.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 5 }}>
          {STEPS.map((s) => <StepCard key={s.badge} {...s} />)}
        </Box>

        {/* Volumen semanal */}
        <SectionTitle>Volumen semanal por grupo muscular</SectionTitle>
        <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${BORDER}`, overflow: 'hidden', mb: 5 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            {VOLUME.map((v, i) => (
              <Box key={v.muscle} sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                px: 2.5, py: 1.25, bgcolor: i % 2 === 0 ? BLUE_SOFT : '#fff',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: BLUE }} />
                  <Typography sx={{ color: DARK, fontFamily: 'Kanit, sans-serif', fontSize: 14.5 }}>{v.muscle}</Typography>
                </Box>
                <Typography sx={{ color: BLUE_HOVER, fontWeight: 700, fontFamily: 'Kanit, sans-serif', fontSize: 14.5 }}>
                  {v.series}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>

        {/* La rutina completa */}
        <SectionTitle>La rutina completa, día por día</SectionTitle>
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained" disableElevation component="a" href={PDF_URL} download
            sx={{
              py: 1.3, px: 3.5, fontFamily: 'Kanit, sans-serif', fontWeight: 700, fontSize: 15,
              textTransform: 'none', bgcolor: BLUE, '&:hover': { bgcolor: BLUE_HOVER },
            }}
          >
            Descargar PDF
          </Button>
        </Box>
        {DAYS.map((d) => <DayBlock key={d.id} day={d} />)}

        {/* CTA a coaching */}
        <Paper elevation={0} sx={{ mt: 5, p: { xs: 3, md: 4 }, borderRadius: 3, bgcolor: DARK }}>
          <Typography sx={{ color: '#fff', fontWeight: 800, fontFamily: 'Kanit, sans-serif', fontSize: { xs: 18, md: 22 }, mb: 0.75 }}>
            ¿Quieres un programa de verdad, hecho para ti?
          </Typography>
          <Typography sx={{ color: '#CBD5E1', fontFamily: 'Kanit, sans-serif', fontSize: 14.5, mb: 2.5, maxWidth: 640 }}>
            Esta rutina es el punto de partida. En AU Coaching armamos tu programa completo —entrenamiento, nutrición,
            descanso y seguimiento real— y lo ajustamos semana a semana según cómo respondes.
          </Typography>
          <Button
            variant="contained" disableElevation onClick={() => navigate('/form')}
            sx={{
              py: 1.3, px: 3.5, fontFamily: 'Kanit, sans-serif', fontWeight: 700, fontSize: 15,
              textTransform: 'none', bgcolor: BLUE, '&:hover': { bgcolor: BLUE_HOVER },
            }}
          >
            Postula a AU Coaching
          </Button>
        </Paper>
        </>
        )}
      </Container>

      <VideoAccessForm
        open={showLeadForm}
        onClose={() => setShowLeadForm(false)}
        onSubmit={handleLeadSubmit}
        title="Desbloquea el split de 5 días"
        subtitle="Déjanos tus datos y te mostramos la rutina completa al instante, con el PDF para descargar."
        submitLabel="Ver la rutina completa"
      />
    </Box>
  );
};

export default FiveDaySplitPage;
